import React, { useCallback, useMemo, useState } from "react";
import { queryCache } from "react-query";
import clsx from "clsx";
import Grid from "@material-ui/core/Grid";
import OverflowTooltip from "../../../../components/OverflowTooltip/OverflowTooltip";
import { ActionBar } from "../../../../components/ActionBar/ActionBar";
import { QUERY_KEYS } from "../../../../constants";
import { useWizard } from "../../../wizard/context";
import { parseProgramListItemSecondaryToFrontEndModel } from "../../helpers/parseProgramListItemSecondaryToFrontEndModel";
import { useAcademicsSecondaryContext } from "../../pages/AcademicsPageSecondary";
import { useProgramStyles } from "../../styles/useProgramStyles";
import { IProgramListItemHigherBackEnd } from "../../types";
import { IntakesListSecondary } from "./IntakesListSecondary";
import { FinanceViewSecondary } from "./FinanceViewSecondary";
import { Accordion, AccordionDetails, AccordionSummary } from "./Accordion";

enum ProgramExpandedState {
  Closed = 0,
  Tuition = 1,
  Intake = 2,
}

export function ProgramSecondary({
  programBack,
}: {
  programBack: IProgramListItemHigherBackEnd;
}) {
  const programFront = useMemo(
    () => parseProgramListItemSecondaryToFrontEndModel(programBack),
    [programBack],
  );
  const classes = useProgramStyles();
  const { openWizard } = useWizard();
  const [programExpandedState, setProgramExpandedState] =
    useState<ProgramExpandedState>(ProgramExpandedState.Closed);
  const {
    refetchAcademics,
    programWizardConfigs: {
      getViewConfig,
      getEditConfig,
      getDeleteConfig,
      getActivateConfig,
      getDeactivateConfig,
    },
  } = useAcademicsSecondaryContext();

  const finance = {
    tuition: programFront.tuition,
    commission: programFront.commissionAmount,
    otherFees: programFront.otherFees,
    scholarship: programFront.scholarship,
    financialAid: programFront.financialAid,
    boardingAmount: programFront.boardingAmount,
    homestayAmount: programFront.homestayAmount,
    insuranceAmount: programFront.insuranceAmount,
  };

  const handleIntakeClick = () => {
    if (programExpandedState !== ProgramExpandedState.Intake) {
      setProgramExpandedState(ProgramExpandedState.Intake);
    } else {
      setProgramExpandedState(ProgramExpandedState.Closed);
    }
  };

  const handleTuitionClick = () => {
    if (programExpandedState !== ProgramExpandedState.Tuition) {
      setProgramExpandedState(ProgramExpandedState.Tuition);
    } else {
      setProgramExpandedState(ProgramExpandedState.Closed);
    }
  };

  const { schoolStudentApplications, majorsContainingPrograms } = QUERY_KEYS;

  const changeProgramStatus = useCallback(async () => {
    if (programFront.isActive) {
      (await openWizard(getDeactivateConfig(programFront.id))) &&
        (await refetchAcademics());
      queryCache.invalidateQueries([schoolStudentApplications]);
    } else {
      (await openWizard(getActivateConfig(programFront.id))) &&
        (await refetchAcademics());
      queryCache.invalidateQueries([schoolStudentApplications]);
    }
  }, [programFront]);

  const deleteProgram = useCallback(async () => {
    (await openWizard(getDeleteConfig(programFront.id))) &&
      (await refetchAcademics());
    queryCache.invalidateQueries([majorsContainingPrograms]);
  }, [programFront]);

  const viewProgram = useCallback(
    () => openWizard(getViewConfig(programFront.id)),
    [programFront],
  );
  const editProgram = useCallback(async () => {
    (await openWizard(getEditConfig(programFront.id))) &&
      (await refetchAcademics());
  }, [programFront]);

  const housingTypes = programFront.housingTypes?.length
    ? programFront.housingTypes?.join(", ")
    : "N/A";

  return (
    <div>
      <Accordion
        className={classes.mainContainer}
        expanded={!!programExpandedState}
        TransitionProps={{
          timeout: 0,
        }}
      >
        <AccordionSummary>
          <Grid
            container
            spacing={2}
            alignItems={"center"}
            className={classes.gridContainer}
          >
            <Grid item xs={12} sm={6} md={3} className={classes.content}>
              <div className={classes.labeledContent}>
                <div>Program Type</div>
                <div>{programFront.programType}</div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={4} className={classes.content}>
              <div className={classes.labeledContent}>
                <div>Housing Type</div>
                <div>
                  <OverflowTooltip text={housingTypes} />
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={2} className={classes.content}>
              <div className={classes.labeledContent}>
                <div>Intake</div>
                <div>
                  <span className={classes.link} onClick={handleIntakeClick}>
                    See Details
                  </span>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={2} className={classes.content}>
              <div className={classes.labeledContent}>
                <div>Tuition</div>
                <div>
                  <span className={classes.link} onClick={handleTuitionClick}>
                    See Details
                  </span>
                </div>
              </div>
            </Grid>
          </Grid>
          <ActionBar
            viewCallBack={viewProgram}
            viewTooltip="View program"
            editCallBack={editProgram}
            editTooltip="Edit program"
            switchCallBack={changeProgramStatus}
            switchTooltipActive="Deactivate program"
            switchTooltipNotActive="Activate program"
            switchState={programFront.isActive}
            deleteCallBack={deleteProgram}
            deleteTooltip={"Delete program"}
          />
        </AccordionSummary>
        <AccordionDetails>
          <div className={clsx(!programFront.isActive && classes.disabled)}>
            {programExpandedState === ProgramExpandedState.Intake && (
              <IntakesListSecondary intakes={programFront.intakes} />
            )}
            {programExpandedState === ProgramExpandedState.Tuition && (
              <FinanceViewSecondary {...finance} />
            )}
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
